import React, { useState, useEffect } from 'react';
import ClinicCard from '../components/ClinicCard';
import SearchInput from '../components/SearchInput';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import ContactField from '../components/ContactField';
import ShareModal from '../components/ShareModal';
import { formatCityDisplay, generateClinicSlug } from '../utils/helpers';
import { updateOpenGraphTags } from '../utils/opengraph';

const ClinicDetail = ({
  clinic, backendUrl,
  navigateToHome, navigateToBlog, navigateToB2B, navigateToPrivacy, navigateToTerms, navigateToAbout, navigateToSitemap,
  onViewClinic, openShareModal, isMobileMenuOpen, setIsMobileMenuOpen,
  showShareModal, setShowShareModal, shareUrl, shareTitle, copyToClipboard, shareToFacebook, shareToX, shareToLinkedIn, shareViaEmail,
  publicSettings,
  searchFilters, setSearchFilters, handleCitySelect, handleAdvancedSearch,
  showAdvancedSearch, setShowAdvancedSearch, counties, budapestDistricts,
  services, showServicesPanel, setShowServicesPanel, servicesRef, toggleService,
  onBackToSearch
}) => {
  // Lightbox state for gallery images
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImageIndex, setLightboxImageIndex] = useState(0);

  const openLightbox = (index) => {
    setLightboxImageIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    if (clinic.images && lightboxImageIndex < clinic.images.length - 1) {
      setLightboxImageIndex(lightboxImageIndex + 1);
    }
  };

  const prevImage = () => {
    if (lightboxImageIndex > 0) {
      setLightboxImageIndex(lightboxImageIndex - 1);
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, lightboxImageIndex]);

  // Dynamic SEO setup for clinic pages - PRIORITIZE CUSTOM SEO FIELDS
  useEffect(() => {
    console.log('🔥 ClinicDetail SEO useEffect triggered!', {
      clinic: clinic ? clinic.name : 'no clinic',
      hasMetaTitle: clinic ? !!clinic.meta_title : false,
      hasMetaDescription: clinic ? !!clinic.meta_description : false,
      hasOgImage: clinic ? !!clinic.og_image : false
    });
    
    if (!clinic) {
      console.log('❌ No clinic data available for SEO setup');
      return;
    }
    
    // 🎯 PRIORITY 1: Use custom meta_title from admin if available
    let title;
    if (clinic.meta_title && clinic.meta_title.trim()) {
      title = clinic.meta_title.trim();
      console.log('🎯 Using custom meta_title:', title);
    } else {
      // FALLBACK: Generate dynamic Meta Title: "Fogorvos Város Kerület(ha van, arab számmal) Név"
      title = "Fogorvos";
      
      if (clinic.city) {
        title += ` ${clinic.city}`;
      }
      
      // Convert Roman district to Arabic number if exists
      if (clinic.district) {
        let district = clinic.district;
        // Convert Roman numerals to Arabic numbers
        const romanToArabic = {
          'I. kerület': '1. kerület', 'II. kerület': '2. kerület', 'III. kerület': '3. kerület', 'IV. kerület': '4. kerület', 'V. kerület': '5. kerület',
          'VI. kerület': '6. kerület', 'VII. kerület': '7. kerület', 'VIII. kerület': '8. kerület', 'IX. kerület': '9. kerület', 'X. kerület': '10. kerület',
          'XI. kerület': '11. kerület', 'XII. kerület': '12. kerület', 'XIII. kerület': '13. kerület', 'XIV. kerület': '14. kerület', 'XV. kerület': '15. kerület',
          'XVI. kerület': '16. kerület', 'XVII. kerület': '17. kerület', 'XVIII. kerület': '18. kerület', 'XIX. kerület': '19. kerület', 'XX. kerület': '20. kerület',
          'XXI. kerület': '21. kerület', 'XXII. kerület': '22. kerület', 'XXIII. kerület': '23. kerület'
        };
        
        // Find and replace Roman numerals with full "kerület" text
        for (const [roman, arabic] of Object.entries(romanToArabic)) {
          if (district.includes(roman)) {
            district = arabic;
            break;
          }
        }
        
        title += ` ${district}`;
      }
      
      if (clinic.name) {
        title += ` - ${clinic.name}`;
      }
      console.log('🔄 Using generated meta title:', title);
    }
    
    document.title = title;
    console.log('🎯 DOCUMENT TITLE SET TO:', title);
    
    // 🎯 PRIORITY 1: Use custom meta_description from admin if available
    let description;
    if (clinic.meta_description && clinic.meta_description.trim()) {
      description = clinic.meta_description.trim();
      console.log('🎯 Using custom meta_description:', description);
    } else {
      // FALLBACK: Generate dynamic Meta Description: "Rólunk szövege, Szolgáltatások vesszővel elválasztva. Pontos cím Irányítószám Város utca Házszám"
      description = "";
      
      // Add "Rólunk" text (about text)
      if (clinic.about) {
        description += clinic.about;
      }
      
      // Add services separated by commas
      if (clinic.services && clinic.services.length > 0) {
        if (description) description += ". ";
        description += clinic.services.join(", ");
      }
      
      // Add address information
      if (clinic.address || clinic.postal_code || clinic.city) {
        if (description) description += ". ";
        let address = "";
        if (clinic.postal_code) address += clinic.postal_code;
        if (clinic.city) address += (address ? " " : "") + clinic.city;
        if (clinic.address) address += (address ? ", " : "") + clinic.address;
        description += address;
      }
      console.log('🔄 Using generated meta description:', description);
    }
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.getElementsByTagName('head')[0].appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', description);
    console.log('🎯 META DESCRIPTION SET TO:', description);
    
    // Add keywords meta tag with clinic specific terms
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.getElementsByTagName('head')[0].appendChild(metaKeywords);
    }
    
    let keywords = `fogorvos, fogászat, ${clinic.city || ''}`;
    if (clinic.services && clinic.services.length > 0) {
      keywords += `, ${clinic.services.slice(0, 5).join(', ')}`;
    }
    metaKeywords.setAttribute('content', keywords);
    
    // Update OpenGraph meta tags for clinic pages
    // 🎯 PRIORITY 1: Use custom og_image from admin if available
    let clinicImage;
    if (clinic.og_image && clinic.og_image.trim()) {
      clinicImage = clinic.og_image.trim();
      console.log('🎯 Using custom og_image:', clinicImage);
    } else if (clinic.images && clinic.images.length > 0) {
      clinicImage = clinic.images[0];
      console.log('🔄 Using first clinic image as fallback:', clinicImage);
    } else {
      clinicImage = null;
      console.log('🔄 No clinic-specific image, using default OG image');
    }
    
    updateOpenGraphTags({
      title: title,
      description: description,
      type: 'website',
      url: window.location.href,
      image: clinicImage,
      imageAlt: `${clinic.name} - Fogorvos klinika`
    }, publicSettings.default_og_image);
    
    // Cleanup function to reset meta tags when component unmounts
    return () => {
      document.title = "Találja meg a legjobb fogorvost – FogPortál.hu országos kereső";
      if (metaDescription) {
        metaDescription.setAttribute('content', 'Fogászat kereső és fogorvos kereső országos lefedettséggel. Részletes adatok, szakterületek, nyitvatartás – minden, ami segít a választásban.');
      }
    };
  }, [clinic, publicSettings.default_og_image]);
  
  // Build clinic meta data
  const clinicLocation = formatCityDisplay(clinic.city, clinic.district);
  const clinicSeoTitle = clinic.meta_title || `${clinic.name} – Fogorvos ${clinicLocation} | FogPortál.hu`;
  const clinicSeoDesc = clinic.meta_description || clinic.short_description || `${clinic.name} fogászati rendelő ${clinicLocation}. Szolgáltatások, nyitvatartás, kapcsolat – FogPortál.hu`;
  const clinicOgImage = clinic.og_image 
    ? (clinic.og_image.startsWith('http') ? clinic.og_image : `${backendUrl}${clinic.og_image}`)
    : (clinic.images && clinic.images.length > 0 
      ? (clinic.images[0].startsWith('http') ? clinic.images[0] : `${backendUrl}${clinic.images[0]}`) 
      : null);
  const clinicSlug = generateClinicSlug(clinic);
  const clinicCanonical = `${window.location.origin}/clinic/${clinicSlug}`;

  const clinicSchema = {
    "@context": "https://schema.org",
    "@type": "Dentist",
    "name": clinic.name,
    "description": clinicSeoDesc,
    "url": clinicCanonical,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": clinic.address || "",
      "addressLocality": clinic.city || "",
      "addressRegion": clinic.district || "",
      "postalCode": clinic.zip || "",
      "addressCountry": "HU"
    },
    ...(clinic.phone && { "telephone": clinic.phone }),
    ...(clinic.email && { "email": clinic.email }),
    ...(clinicOgImage && { "image": clinicOgImage }),
    ...(clinic.google_rating && { 
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": clinic.google_rating,
        "reviewCount": clinic.google_reviews_count || 1
      }
    }),
    "inLanguage": "hu-HU"
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead 
        title={clinicSeoTitle}
        description={clinicSeoDesc}
        keywords={`${clinic.name}, fogorvos ${clinicLocation}, fogászat ${clinic.city || ''}, ${(clinic.services || []).join(', ')}`}
        ogImage={clinicOgImage}
        ogType="business.business"
        canonicalUrl={clinicCanonical}
        schema={clinicSchema}
      />
      {/* Modern Header */}
      <header className="bg-white shadow-lg border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <div className="flex items-center">
            <button 
              onClick={navigateToHome}
              className="flex items-center space-x-3"
            >
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-green-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 2.21 1.79 4 4 4s4-1.79 4-4v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7zm2 15c0 1.1-.9 2-2 2s-2-.9-2-2v-1h4v1zm1.46-4.86C14.8 12.64 14 13.26 14 14v1h-4v-1c0-.74-.8-1.36-1.46-.86C7.06 12.5 6 10.84 6 9c0-3.31 2.69-6 6-6s6 2.69 6 6c0 1.84-1.06 3.5-2.54 4.14z"/>
                </svg>
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
                FogPortál.hu
              </h1>
            </button>
          </div>

          {/* Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            <button 
              onClick={navigateToHome}
              className="px-4 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg font-medium text-sm transition-all duration-200"
            >
              Főoldal
            </button>
            <button 
              onClick={navigateToB2B}
              className="px-4 py-2 bg-gradient-to-r from-blue-600 to-green-600 text-white rounded-lg font-medium text-sm hover:from-blue-700 hover:to-green-700 transition-all duration-200 shadow-md hover:shadow-lg ml-2"
            >
              Megjelenés fogászatoknak
            </button>
          </nav>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-lg text-gray-600 hover:text-blue-600 hover:bg-gray-50"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} 
                />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Mobile menu dropdown */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-gray-200">
            <div className="flex flex-col space-y-2 pt-4 px-4">
              <button 
                onClick={() => {
                  navigateToHome();
                  setIsMobileMenuOpen(false);
                }}
                className="px-4 py-3 text-left text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg font-medium transition-all duration-200"
              >
                Főoldal
              </button>
              <button 
                onClick={() => {
                  navigateToB2B();
                  setIsMobileMenuOpen(false);
                }}
                className="px-4 py-3 text-left text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg font-medium transition-all duration-200"
              >
                Megjelenés fogászatoknak
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
    
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white p-8">
          <div className="flex items-center space-x-6">
            {clinic.logo && (
              <img 
                src={`${backendUrl}${clinic.logo}`} 
                alt={clinic.name}
                className="w-24 h-24 bg-white rounded-lg p-2 object-contain"
              />
            )}
            <div>
              <h1 className="text-4xl font-bold">{clinic.name}</h1>
              <p className="text-xl mt-2">{formatCityDisplay(clinic.city, clinic.district)}</p>
            </div>
          </div>
        </div>
        
        {/* Back to Search Button */}
        {sessionStorage.getItem('fogportal_search_state') && (
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mx-8 mt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span className="text-blue-800 font-medium">Keresési eredményekből érkezik</span>
              </div>
              <button
                onClick={onBackToSearch}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>Vissza a találatokhoz</span>
              </button>
            </div>
          </div>
        )}
        
        {/* Content */}
        <div className="p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column */}
            <div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Rólunk</h2>
              <p className="text-gray-600 mb-6">{clinic.description}</p>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Szolgáltatások</h3>
              <div className="flex flex-wrap gap-2 mb-6">
                {clinic.services.map((service, index) => (
                  <span key={index} className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                    {service}
                  </span>
                ))}
              </div>

              {/* Bemutatkozó videó szekció */}
              {clinic.intro_video_url && (
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">📹 Bemutatkozó videó</h3>
                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="relative" style={{ paddingBottom: '56.25%', height: 0 }}>
                      <iframe
                        src={(() => {
                          try {
                            let videoId = '';
                            const url = clinic.intro_video_url;
                            
                            // YouTube rövidített link (youtu.be)
                            if (url.includes('youtu.be/')) {
                              videoId = url.split('youtu.be/')[1].split('?')[0].split('&')[0];
                            }
                            // YouTube normál link (youtube.com)
                            else if (url.includes('youtube.com/watch')) {
                              const urlParams = new URL(url).searchParams;
                              videoId = urlParams.get('v');
                            }
                            
                            return videoId ? `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&controls=1` : '';
                          } catch (e) {
                            return '';
                          }
                        })()}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="absolute top-0 left-0 w-full h-full rounded-lg"
                        title={`${clinic.name} bemutatkozó videó`}
                      />
                    </div>
                    <p className="text-sm text-gray-500 mt-3">
                      Ismerje meg rendelőnket részletesebben a bemutatkozó videónkban.
                    </p>
                  </div>
                </div>
              )}

              {/* Google Reviews Section - Moved to left column after video */}
              {clinic && clinic.google_rating && clinic.google_review_count && clinic.google_review_count > 0 && (
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">Google Értékelések</h3>
                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <span className="text-3xl font-bold text-gray-900 mr-3">{clinic.google_rating.toFixed(1)}</span>
                        <div>
                          <div className="flex mb-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <svg 
                                key={star} 
                                className={`w-5 h-5 ${star <= Math.round(clinic.google_rating) ? 'text-yellow-400' : 'text-gray-300'}`}
                                fill="currentColor" 
                                viewBox="0 0 20 20"
                              >
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            ))}
                          </div>
                          <p className="text-sm text-gray-600">Google értékelések alapján</p>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <a 
                          href={clinic.google_business_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                        >
                          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                          {clinic.google_review_count} vélemény
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {clinic.images && clinic.images.length > 0 && (
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">Galéria</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {clinic.images.map((image, index) => (
                      <div 
                        key={index}
                        className="relative group cursor-pointer overflow-hidden rounded-lg"
                        onClick={() => openLightbox(index)}
                      >
                        <img 
                          src={`${backendUrl}${image}`}
                          alt={`${clinic.name} kép ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg transition-transform duration-300 group-hover:scale-110"
                        />
                        {/* Hover overlay */}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                          <svg 
                            className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                          </svg>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Lightbox Modal */}
              {lightboxOpen && clinic.images && (
                <div 
                  className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
                  onClick={closeLightbox}
                >
                  {/* Close button */}
                  <button
                    onClick={closeLightbox}
                    className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-50"
                  >
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>

                  {/* Previous button */}
                  {lightboxImageIndex > 0 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); prevImage(); }}
                      className="absolute left-4 text-white hover:text-gray-300 transition-colors z-50"
                    >
                      <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                  )}

                  {/* Image */}
                  <div className="max-w-6xl max-h-full" onClick={(e) => e.stopPropagation()}>
                    <img 
                      src={`${backendUrl}${clinic.images[lightboxImageIndex]}`}
                      alt={`${clinic.name} kép ${lightboxImageIndex + 1}`}
                      className="max-w-full max-h-[90vh] object-contain rounded-lg"
                    />
                    {/* Image counter */}
                    <div className="text-center text-white mt-4">
                      <p className="text-lg">
                        {lightboxImageIndex + 1} / {clinic.images.length}
                      </p>
                    </div>
                  </div>

                  {/* Next button */}
                  {lightboxImageIndex < clinic.images.length - 1 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); nextImage(); }}
                      className="absolute right-4 text-white hover:text-gray-300 transition-colors z-50"
                    >
                      <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  )}
                </div>
              )}
            </div>
            
            {/* Right Column */}
            <div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Kapcsolat</h2>
              <div className="space-y-4 mb-6">
                <div>
                  <h4 className="font-semibold text-gray-800">Cím:</h4>
                  <p className="text-gray-600">{clinic.address}</p>
                </div>
                
                {/* Protected Contact Fields */}
                <ContactField 
                  label="Telefon" 
                  value={clinic.phone} 
                  type="phone" 
                  clinicId={clinic.id} 
                />
                
                <ContactField 
                  label="Email" 
                  value={clinic.email} 
                  type="email" 
                  clinicId={clinic.id} 
                />
              </div>
                
                {/* Website and Social Media Links */}
                {(clinic.website || clinic.facebook_url || clinic.instagram_url || clinic.linkedin_url || clinic.youtube_url) && (
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-800 mb-3">Online elérhetőségek:</h4>
                    <div className="flex flex-wrap gap-4">
                      {clinic.website && (
                        <a 
                          href={clinic.website} 
                          target="_blank" 
                          rel="noopener noreferrer nofollow"
                          className="flex items-center space-x-2 bg-blue-100 text-blue-800 px-3 py-2 rounded-lg hover:bg-blue-200 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.083 9h1.946c.089-1.546.383-2.97.837-4.118A6.004 6.004 0 004.083 9zM10 2a8 8 0 100 16 8 8 0 000-16zm0 2c-.076 0-.232.032-.465.262-.238.234-.497.623-.737 1.182-.389.907-.673 2.142-.766 3.556h3.936c-.093-1.414-.377-2.649-.766-3.556-.24-.56-.5-.948-.737-1.182C10.232 4.032 10.076 4 10 4zm3.971 5c-.089-1.546-.383-2.97-.837-4.118A6.004 6.004 0 0115.917 9h-1.946zm-2.003 2H8.032c.093 1.414.377 2.649.766 3.556.24.56.5.948.737 1.182.233.23.389.262.465.262.076 0 .232-.032.465-.262.238-.234.498-.623.737-1.182.389-.907.673-2.142.766-3.556zm1.166 4.118c.454-1.148.748-2.572.837-4.118h1.946a6.004 6.004 0 01-2.783 4.118zm-6.268 0C6.412 13.97 6.118 12.546 6.03 11H4.083a6.004 6.004 0 002.783 4.118z" clipRule="evenodd" />
                          </svg>
                          <span>Weboldal</span>
                        </a>
                      )}
                      
                      {clinic.facebook_url && (
                        <a 
                          href={clinic.facebook_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center space-x-2 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M20 10c0-5.523-4.477-10-10-10S0 4.477 0 10c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V10h2.54V7.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V10h2.773l-.443 2.89h-2.33v6.988C16.343 19.128 20 14.991 20 10z" clipRule="evenodd" />
                          </svg>
                          <span>Facebook</span>
                        </a>
                      )}
                      
                      {clinic.instagram_url && (
                        <a 
                          href={clinic.instagram_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center space-x-2 bg-gradient-to-r from-pink-500 to-yellow-500 text-white px-3 py-2 rounded-lg hover:from-pink-600 hover:to-yellow-600 transition-all"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 0C4.478 0 0 4.478 0 10s4.478 10 10 10 10-4.478 10-10S15.522 0 10 0zm4.2 6.4c.4 0 .8.4.8.8v.8c0 .4-.4.8-.8.8h-.8c-.4 0-.8-.4-.8-.8v-.8c0-.4.4-.8.8-.8h.8zM10 6c2.2 0 4 1.8 4 4s-1.8 4-4 4-4-1.8-4-4 1.8-4 4-4zm0 1.5c-1.4 0-2.5 1.1-2.5 2.5s1.1 2.5 2.5 2.5 2.5-1.1 2.5-2.5-1.1-2.5-2.5-2.5zM10 2c2.2 0 2.5 0 3.4.1.8 0 1.2.2 1.5.3.4.1.6.3.9.6.3.3.5.5.6.9.1.3.2.7.3 1.5.1.9.1 1.2.1 3.4s0 2.5-.1 3.4c0 .8-.2 1.2-.3 1.5-.1.4-.3.6-.6.9-.3.3-.5.5-.9.6-.3.1-.7.2-1.5.3-.9.1-1.2.1-3.4.1s-2.5 0-3.4-.1c-.8 0-1.2-.2-1.5-.3-.4-.1-.6-.3-.9-.6-.3-.3-.5-.5-.6-.9-.1-.3-.2-.7-.3-1.5C2 12.5 2 12.2 2 10s0-2.5.1-3.4c0-.8.2-1.2.3-1.5.1-.4.3-.6.6-.9.3-.3.5-.5.9-.6.3-.1.7-.2 1.5-.3C7.5 2 7.8 2 10 2z" clipRule="evenodd" />
                          </svg>
                          <span>Instagram</span>
                        </a>
                      )}
                      
                      {clinic.linkedin_url && (
                        <a 
                          href={clinic.linkedin_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center space-x-2 bg-blue-700 text-white px-3 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z" clipRule="evenodd" />
                          </svg>
                          <span>LinkedIn</span>
                        </a>
                      )}
                      
                      {clinic.youtube_url && (
                        <a 
                          href={clinic.youtube_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center space-x-2 bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-700 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"/>
                            <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"/>
                          </svg>
                          <span>YouTube</span>
                        </a>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-800 mb-3">Nyitvatartás:</h4>
                  <p className="text-gray-600 whitespace-pre-line leading-relaxed">{clinic.opening_hours}</p>
                </div>
                
                {/* Appointment Booking Section - RIGHT COLUMN, AFTER OPENING HOURS */}
                <div className="bg-gray-100 p-6 rounded-lg mt-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">Időpontfoglalás</h3>
                  <p className="text-gray-600 mb-4">
                    Hívja fel a klinikát közvetlenül az időpontfoglaláshoz:
                  </p>
                  <a 
                    href={`tel:${clinic.phone}`}
                    className="block w-full bg-gradient-to-r from-green-500 to-blue-600 text-white text-center py-4 px-6 rounded-xl hover:from-green-600 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 font-semibold shadow-lg hover:shadow-xl"
                  >
                    📞 {clinic.phone}
                  </a>
                  
                  {/* Online Booking with Foglaljorvost */}
                  {clinic.foglaljorvost_link && (
                    <div className="mt-4">
                      <p className="text-center text-gray-600 mb-3">
                        Vagy foglalj online
                      </p>
                      <a
                        href={clinic.foglaljorvost_link}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="block w-full bg-white border-2 border-gray-200 hover:border-green-500 rounded-xl p-3 sm:p-4 transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg group"
                      >
                        <div className="flex items-center justify-center space-x-2 sm:space-x-3">
                          <img 
                            src="/foglaljorvost_logo.svg" 
                            alt="Foglaljorvost.hu" 
                            className="h-6 sm:h-8 w-auto flex-shrink-0 transition-transform duration-300 group-hover:scale-110"
                          />
                          <span className="text-gray-700 group-hover:text-green-600 font-medium transition-colors duration-300 text-sm sm:text-base text-center">
                            Online időpontfoglalás
                          </span>
                        </div>
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Search Similar Doctors Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">
              Keressen további hasonló fogorvosokat
            </h2>
            <p className="text-lg text-gray-600">
              Fogportál.hu - az országos fogorvos kereső
            </p>
          </div>
          
          {/* Search Box */}
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-6xl mx-auto">
            {/* Basic Search Row */}
            <div className="flex flex-wrap gap-3 mb-4">
              {/* City Autocomplete - New Hook-Based SearchInput */}
              <SearchInput
                placeholder="Város keresése..."
                onCitySelect={handleCitySelect}
                backendUrl={backendUrl}
              />
              
              {/* County Selector */}
              <div className="flex-1 min-w-[180px]">
                <select 
                  value={searchFilters.county}
                  onChange={(e) => setSearchFilters(prev => ({ ...prev, county: e.target.value, city: '', district: '' }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700"
                >
                  <option value="">vagy válasszon vármegyét</option>
                  {counties.map(county => (
                    <option key={county} value={county}>{county}</option>
                  ))}
                </select>
              </div>
              
              {/* District Selector - Only shows for Budapest */}
              {searchFilters.city && searchFilters.city.toLowerCase().includes('budapest') && (
                <div className="flex-1 min-w-[160px]">
                  <select 
                    value={searchFilters.district}
                    onChange={(e) => setSearchFilters(prev => ({ ...prev, district: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700"
                  >
                    <option value="">Válasszon kerületet</option>
                    {budapestDistricts.map(district => (
                      <option key={district.id} value={district.name}>{district.name}</option>
                    ))}
                  </select>
                </div>
              )}
              
              {/* Search Buttons */}
              <div className="flex gap-2 flex-shrink-0">
                <button 
                  onClick={handleAdvancedSearch}
                  className="bg-gradient-to-r from-blue-600 to-green-600 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-green-700 transition-all duration-200 font-semibold shadow-md hover:shadow-lg whitespace-nowrap"
                >
                  Keresés
                </button>
                
                <button
                  type="button"
                  onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
                  className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700 font-medium flex items-center whitespace-nowrap"
                >
                  <svg className={`w-5 h-5 transform transition-transform ${showAdvancedSearch ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  <span className="ml-2">Részletes keresés</span>
                </button>
              </div>
            </div>
            
            {/* Advanced Search Panel - Collapsible */}
            {showAdvancedSearch && (
              <div className="border-t border-gray-200 pt-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  {/* Free Text Search */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Általános keresés</label>
                    <input 
                      type="text"
                      value={searchFilters.freeText}
                      onChange={(e) => setSearchFilters(prev => ({ ...prev, freeText: e.target.value }))}
                      placeholder="Keresés leírásban, címben..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700"
                    />
                  </div>
                  
                  {/* Name Search */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Klinika neve</label>
                    <input 
                      type="text"
                      value={searchFilters.nameSearch}
                      onChange={(e) => setSearchFilters(prev => ({ ...prev, nameSearch: e.target.value }))}
                      placeholder="Keresés klinika nevében..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700"
                    />
                  </div>
                  
                  {/* Services Panel */}
                  <div ref={servicesRef}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Szolgáltatások</label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowServicesPanel(!showServicesPanel)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700 text-left flex justify-between items-center"
                      >
                        <span>
                          {searchFilters.services.length === 0 
                            ? "Válasszon szolgáltatásokat" 
                            : `${searchFilters.services.length} szolgáltatás kiválasztva`
                          }
                        </span>
                        <svg className={`w-5 h-5 transform transition-transform ${showServicesPanel ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {showServicesPanel && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {services.map(service => (
                            <label key={service} className="flex items-center px-4 py-2 hover:bg-blue-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={searchFilters.services.includes(service)}
                                onChange={() => toggleService(service)}
                                className="mr-3 rounded text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-gray-700">{service}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Additional Options */}
                <div className="flex items-center justify-between">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={searchFilters.openNow}
                      onChange={(e) => setSearchFilters(prev => ({ ...prev, openNow: e.target.checked }))}
                      className="mr-2 rounded text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-gray-700 font-medium">Most nyitva</span>
                  </label>
                  
                  <button
                    onClick={() => {
                      setSearchFilters({
                        city: '',
                        county: '',
                        district: '',
                        services: [],
                        freeText: '',
                        nameSearch: '',
                        openNow: false
                      });
                      setShowAdvancedSearch(false);
                    }}
                    className="text-gray-500 hover:text-gray-700 text-sm font-medium"
                  >
                    Szűrők törlése
                  </button>
                </div>
              </div>
            )}
            
            {/* Selected Filters Display */}
            {(
              (searchFilters.city && searchFilters.city.length >= 3 && !showMunicipalityDropdown) || 
              searchFilters.county || 
              searchFilters.services.length > 0 || 
              searchFilters.freeText || 
              searchFilters.nameSearch
            ) && (
              <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200 mt-4">
                {searchFilters.city && searchFilters.city.length >= 3 && !showMunicipalityDropdown && (
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium flex items-center">
                    {searchFilters.city}
                    <button 
                      onClick={() => setSearchFilters(prev => ({ ...prev, city: '', district: '' }))}
                      className="ml-2 hover:text-blue-600"
                    >
                      ×
                    </button>
                  </span>
                )}
                {searchFilters.county && (
                  <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium flex items-center">
                    {searchFilters.county}
                    <button 
                      onClick={() => setSearchFilters(prev => ({ ...prev, county: '' }))}
                      className="ml-2 hover:text-green-600"
                    >
                      ×
                    </button>
                  </span>
                )}
                {searchFilters.services.map(service => (
                  <span key={service} className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium flex items-center">
                    {service}
                    <button 
                      onClick={() => toggleService(service)}
                      className="ml-2 hover:text-purple-600"
                    >
                      ×
                    </button>
                  </span>
                ))}
                {searchFilters.freeText && (
                  <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium flex items-center">
                    "{searchFilters.freeText}"
                    <button 
                      onClick={() => setSearchFilters(prev => ({ ...prev, freeText: '' }))}
                      className="ml-2 hover:text-orange-600"
                    >
                      ×
                    </button>
                  </span>
                )}
                {searchFilters.nameSearch && (
                  <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium flex items-center">
                    Név: "{searchFilters.nameSearch}"
                    <button 
                      onClick={() => setSearchFilters(prev => ({ ...prev, nameSearch: '' }))}
                      className="ml-2 hover:text-red-600"
                    >
                      ×
                    </button>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <Footer 
        navigateToHome={navigateToHome}
        navigateToBlog={navigateToBlog}
        navigateToB2B={navigateToB2B}
        navigateToPrivacy={() => window.location.href = '/adatvedelem'}
        navigateToTerms={() => window.location.href = '/aszf'}
        navigateToAbout={() => window.location.href = '/rolunk'}
        navigateToSitemap={navigateToSitemap}
      />
    </div>
  );
};


export default ClinicDetail;
